import { computed } from '@angular/core';
import {
  signalStoreFeature,
  type,
  withComputed,
  withMethods,
} from '@ngrx/signals';
import { StoryState } from './story.state';
import { Story } from '../../models/story';
import { StateFilter } from '../crud.state';

export const filterItems = (items: Story[], filter: StateFilter) => {
  return items.filter(item => {
    const titleMatch = item.title
      .toLowerCase()
      .includes(filter.query.toLowerCase());
    const publishedMatch =
      filter.published === item.published || filter.published === null;
    const tagsMatch =
      !filter.tags ||
      filter.tags.length === 0 ||
      (item.tags &&
        filter.tags.some(tagId => item.tags && item.tags.includes(tagId)));

    const categoryMatch =
      !filter.category ||
      filter.category.length === 0 ||
      (!!item.category && filter.category.includes(item.category));

    return titleMatch && publishedMatch && tagsMatch && categoryMatch;
  });
};

export function withStorySelectors() {
  return signalStoreFeature(
    {
      state: type<StoryState>(),
    },
    withComputed(({ items, filter }) => ({
      count: computed(() => Object.keys(items()).length),
      asArray: computed(() => Object.values(items()) as Story[]),
      filteredSorted: computed(() => {
        let filtered = Object.values(items());
        if (filter()) {
          filtered = filterItems(filtered, filter());
        }
        return filtered.sort((a, b) =>
          a.sorting < b.sorting ? -1 : 1
        ) as Story[];
      }),
      groupedByCategory: computed(() => {
        const grouped = filterItems(Object.values(items()), filter()).reduce(
          (acc, story) => {
            const { category } = story;
            if (!category) return acc;
            if (!acc[category]) acc[category] = [];
            acc[category].push(story);
            return acc;
          },
          {} as Record<string, Story[]>
        );

        // Sort each group
        Object.values(grouped).forEach(group =>
          group.sort((a: Story, b: Story) => a.sorting - b.sorting)
        );

        return grouped;
      }),
    })),
    withMethods(store => ({
      globalSearch(query: string, tags: string[]): Story[] {
        const itemsArray = Object.values(store.items());
        const tempFilter: StateFilter = { ...store.filter(), query, tags };
        return filterItems(itemsArray, tempFilter);
      },
    }))
  );
}
